import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight, CheckCircle, Circle, Shield, Rocket, Zap,
  Save, CheckSquare, Activity, AlertCircle, Info, X, ExternalLink, Calendar,
  RotateCcw, XCircle, Clock, ChevronUp, Repeat, ChevronDown, ChevronRight, Lock
} from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import IssueIntelPanel from '@/components/council/IssueIntelPanel';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import RockTaskCreator from '@/components/issues/RockTaskCreator';
import RockActivityCreator from '@/components/issues/RockActivityCreator';
import PolicyDialog from '@/components/issues/PolicyDialog';
import CapabilityDialog from '@/components/issues/CapabilityDialog';

// ─── Constants ──────────────────────────────────────────────────────────
const BALLOON_APPROACHES = [
  { value: 'reassign', label: 'Reassign', icon: '🔄' },
  { value: 'process_adjustment', label: 'Process Fix', icon: '⚙️' },
  { value: 'policy_tweak', label: 'Policy Tweak', icon: '📋' },
  { value: 'communication_fix', label: 'Comms Fix', icon: '📡' },
  { value: 'deprioritize_monitor', label: 'Monitor', icon: '👁️' },
];

const TRACK2_APPROACHES = [
  { value: 'build_internally', label: 'Build' },
  { value: 'contract_service', label: 'Contract' },
  { value: 'acquire', label: 'Acquire' },
  { value: 'build_alliance', label: 'Alliance' },
];

const TRACK3_APPROACHES = [
  { value: 'maneuver_around', label: 'Maneuver' },
  { value: 'retreat', label: 'Retreat' },
  { value: 'support_capability_decision', label: 'Support' },
];

const TRACK_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: 'slate' },
  { value: 'in_progress', label: 'In Progress', color: 'amber' },
  { value: 'completed', label: 'Completed', color: 'emerald' },
];

const ROCK_TRACKS = [
  { key: 'track1', label: 'Bridging Policy', icon: Shield, color: 'blue', question: 'What guardrails help preventing damage or creating the right conditions to build the capability?', approaches: null },
  { key: 'track2', label: 'Capability', icon: Rocket, color: 'emerald', question: 'What capability do we need & how to get it?', approaches: TRACK2_APPROACHES },
  { key: 'track3', label: 'Operational', icon: Zap, color: 'amber', question: 'What do we do right now to either mitigate impact or supporting the capability build and/or policy?', approaches: TRACK3_APPROACHES },
];

// ─── Phase 4 Actions (CiC/Commander gated) ──────────────────────────────
// Each action records the decision on the Issue entity and notifies the submitter.
// All five actions share the same Phase4ActionPanel pattern: reason chip → notes → confirm.
const PHASE4_ACTIONS = [
  {
    value: 'send_back',
    label: 'Send Back',
    icon: RotateCcw,
    color: 'amber',
    description: 'Return to investigation. The issue stays a signal but un-classifies gate2 — it returns to Phase 3 in the next session for fresh density assessment.',
    reasons: [
      { value: 'needs_more_data', label: 'Needs more data' },
      { value: 'needs_stakeholder_input', label: 'Needs stakeholder input' },
      { value: 'context_changed', label: 'Context has changed since classification' },
      { value: 'reclassify_density', label: 'Density classification looks wrong' },
      { value: 'other', label: 'Other' },
    ],
    notification_type: 'issue_sent_back',
  },
  {
    value: 'dismiss',
    label: 'Dismiss',
    icon: XCircle,
    color: 'red',
    description: 'Council considered the issue and decided no action is needed. Sets status to "dismissed".',
    reasons: [
      { value: 'no_longer_relevant', label: 'No longer relevant' },
      { value: 'resolved_elsewhere', label: 'Resolved elsewhere' },
      { value: 'duplicate_of_active', label: 'Duplicate of an active issue' },
      { value: 'cost_exceeds_value', label: 'Cost of action exceeds value' },
      { value: 'out_of_scope', label: 'Out of scope for the council' },
      { value: 'other', label: 'Other' },
    ],
    notification_type: 'issue_dismissed',
  },
  {
    value: 'defer',
    label: 'Defer',
    icon: Clock,
    color: 'indigo',
    description: 'Push this issue to a future session without changing its classification.',
    reasons: [
      { value: 'capacity_constraint', label: 'Council capacity constraint' },
      { value: 'awaiting_prerequisite', label: 'Awaiting a prerequisite decision' },
      { value: 'awaiting_data', label: 'Awaiting data or signal' },
      { value: 'lower_priority', label: 'Lower priority than other signals' },
      { value: 'other', label: 'Other' },
    ],
    notification_type: 'issue_deferred',
  },
  {
    value: 'escalate',
    label: 'Escalate to CiC',
    icon: ChevronUp,
    color: 'purple',
    description: 'Flag for Commander in Chief attention. Sets status to "escalated".',
    reasons: [
      { value: 'cross_org_impact', label: 'Cross-organisation impact' },
      { value: 'strategic_decision', label: 'Strategic-level decision needed' },
      { value: 'resource_authorization', label: 'Resource authorisation needed' },
      { value: 'risk_threshold', label: 'Risk threshold exceeded' },
      { value: 'other', label: 'Other' },
    ],
    notification_type: 'issue_escalated',
  },
  {
    value: 'convert',
    label: 'Convert Type',
    icon: Repeat,
    color: 'sky',
    description: 'Re-classify between balloon and rock. The opposite type takes over and the issue stays in Phase 4.',
    reasons: [
      { value: 'scope_underestimated', label: 'Scope underestimated (balloon → rock)' },
      { value: 'scope_overestimated', label: 'Scope overestimated (rock → balloon)' },
      { value: 'new_information', label: 'New information changes the assessment' },
      { value: 'other', label: 'Other' },
    ],
    notification_type: 'issue_converted',
  },
];

// ─── Role gating ────────────────────────────────────────────────────────
// Mirrors SessionListView.jsx convention (userProfile.custom_role).
// Commander and Commander in Chief can take Phase 4 actions.
function canTakePhase4Actions(userProfile) {
  const role = userProfile?.custom_role;
  return role === 'Commander' || role === 'Commander in Chief';
}

// ─── Helpers ────────────────────────────────────────────────────────────
function getSeverityBadge(issue) {
  const s = issue.severity || issue.impact_level || 'medium';
  const config = {
    critical: 'bg-red-500/15 text-red-400 border-red-500/20',
    high: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
    medium: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
    low: 'bg-slate-500/15 text-slate-400 border-slate-500/20',
  };
  return (
    <Badge variant="outline" className={`text-[9px] font-mono font-bold tracking-wider border ${config[s] || config.medium}`}>
      {s.toUpperCase()}
    </Badge>
  );
}

function isBalloonComplete(issue) {
  return !!(issue.resolution_owner && issue.decision);
}

function isRockComplete(issue) {
  return !!(issue.rock_track1_owner && issue.rock_track2_owner && issue.rock_track3_owner);
}

// ─── Save Toast ─────────────────────────────────────────────────────────
function SaveToast({ show }) {
  if (!show) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-semibold animate-in fade-in-0 slide-in-from-bottom-1 duration-200">
      <CheckCircle className="w-3.5 h-3.5" />
      Saved successfully
    </div>
  );
}

// ─── Progress Ring ──────────────────────────────────────────────────────
function ProgressRing({ progress, size = 44, strokeWidth = 3 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  const strokeColor = progress === 100 ? '#34d399' : progress > 50 ? '#60a5fa' : '#fbbf24';

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={strokeColor} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-500 ease-out" />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        fill={strokeColor} fontSize={size * 0.24} fontWeight="700" fontFamily="monospace"
        className="rotate-90 origin-center">
        {Math.round(progress)}%
      </text>
    </svg>
  );
}

// ─── Approach Chip Selector (replaces dropdowns) ────────────────────────
function ApproachChips({ options, value, onChange, accentColorClass }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const selected = value === opt.value;
        return (
          <button key={opt.value} onClick={() => onChange(opt.value)}
            className={`
              flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150 cursor-pointer border
              ${selected
                ? `${accentColorClass} font-semibold`
                : 'text-slate-400 bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06] hover:border-white/10'}`}
          >
            {opt.icon && <span className="text-sm">{opt.icon}</span>}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Status Chip Selector (replaces dropdowns) ─────────────────────────
function StatusChips({ value, onChange }) {
  return (
    <div className="flex gap-1.5">
      {TRACK_STATUS_OPTIONS.map(s => {
        const selected = value === s.value;
        const colorMap = {
          slate: selected ? 'text-slate-300 bg-slate-500/15 border-slate-500/25' : '',
          amber: selected ? 'text-amber-400 bg-amber-500/15 border-amber-500/25' : '',
          emerald: selected ? 'text-emerald-400 bg-emerald-500/15 border-emerald-500/25' : '',
        };
        return (
          <button key={s.value} onClick={() => onChange(s.value)}
            className={`
              px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer border
              ${selected
                ? `${colorMap[s.color]} font-semibold`
                : 'text-slate-500 bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06]'}`}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Phase4ActionPanel ──────────────────────────────────────────────────
// Renders one of the 5 Phase 4 actions (send back / dismiss / defer / escalate / convert)
// as a collapsible panel. Same pattern as PreTriageRejectionPanel in Phase 2.
//
// On confirm:
//   - Updates Issue entity with phase4_action* fields + applies the action's side effects
//     (un-classify gate2, set status, flip type, etc.)
//   - Appends to phase4_action_history (mirrors rejection_history pattern)
//   - Sends notification to submitter (issue.reported_by)
//   - Closes the detail dialog via onActionTaken
//
// Side effects per action:
//   - send_back: classification_gate2 = null (returns to Phase 3 next session)
//   - dismiss:   status = 'dismissed'
//   - defer:     status = 'deferred'
//   - escalate:  status = 'escalated'
//   - convert:   classification_gate2 flips balloon ↔ rock (stays in Phase 4)
function Phase4ActionPanel({ action, issue, officers, userProfile, onActionTaken }) {
  const queryClient = useQueryClient();
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const Icon = action.icon;
  const colorMap = {
    amber:  { btn: 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10',  panel: 'border-amber-500/25 bg-amber-950/20',   accent: 'text-amber-300',  cta: 'bg-amber-600 hover:bg-amber-700' },
    red:    { btn: 'text-red-400 hover:text-red-300 hover:bg-red-500/10',         panel: 'border-red-500/25 bg-red-950/20',       accent: 'text-red-300',    cta: 'bg-red-600 hover:bg-red-700' },
    indigo: { btn: 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10', panel: 'border-indigo-500/25 bg-indigo-950/20', accent: 'text-indigo-300', cta: 'bg-indigo-600 hover:bg-indigo-700' },
    purple: { btn: 'text-purple-400 hover:text-purple-300 hover:bg-purple-500/10', panel: 'border-purple-500/25 bg-purple-950/20', accent: 'text-purple-300', cta: 'bg-purple-600 hover:bg-purple-700' },
    sky:    { btn: 'text-sky-400 hover:text-sky-300 hover:bg-sky-500/10',          panel: 'border-sky-500/25 bg-sky-950/20',       accent: 'text-sky-300',    cta: 'bg-sky-600 hover:bg-sky-700' },
  };
  const colors = colorMap[action.color] || colorMap.amber;

  const allowed = canTakePhase4Actions(userProfile);
  const selectedReason = action.reasons.find(r => r.value === reason);

  const actionMutation = useMutation({
    mutationFn: async () => {
      const historyEntry = {
        action: action.value,
        taken_by: user?.email,
        taken_at: new Date().toISOString(),
        reason: reason,
        reason_label: selectedReason?.label || reason,
        notes: notes || '',
        previous_status: issue.status || '',
        previous_classification_gate2: issue.classification_gate2 || '',
      };

      // Build entity update payload based on action type
      const updatePayload = {
        phase4_action: action.value,
        phase4_action_reason: reason,
        phase4_action_notes: notes || '',
        phase4_action_taken_by: user?.email,
        phase4_action_taken_at: new Date().toISOString(),
        phase4_action_history: [...(issue.phase4_action_history || []), historyEntry],
      };

      // Side effects per action
      if (action.value === 'send_back') {
        // Un-classify gate2 only — stays a signal, returns to Phase 3 next session
        updatePayload.classification_gate2 = null;
      } else if (action.value === 'dismiss') {
        updatePayload.status = 'dismissed';
      } else if (action.value === 'defer') {
        updatePayload.status = 'deferred';
      } else if (action.value === 'escalate') {
        updatePayload.status = 'escalated';
      } else if (action.value === 'convert') {
        // Flip balloon ↔ rock; stays in Phase 4
        const current = issue.classification_gate2;
        updatePayload.classification_gate2 = current === 'balloon' ? 'rock' : 'balloon';
      }

      await base44.entities.Issue.update(issue.id, updatePayload);

      // Notify submitter (best-effort; never blocks the action)
      if (issue.reported_by) {
        const actor = officers?.find(o => o.user_email === user?.email);
        try {
          await base44.functions.invoke('sendNotification', {
            recipient_email: issue.reported_by,
            type: action.notification_type,
            source_type: 'issue',
            source_id: issue.id,
            source_name: issue.title,
            triggered_by_email: user?.email,
            metadata: {
              issue_title: issue.title,
              action: action.value,
              action_label: action.label,
              reason: selectedReason?.label || reason,
              notes: notes || '',
              actor_name: actor?.callsign || user?.email,
              actor_role: userProfile?.custom_role || '',
            },
          });
        } catch (e) {
          console.warn(`Phase 4 action notification (${action.notification_type}) failed:`, e.message);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setExpanded(false);
      setReason('');
      setNotes('');
      onActionTaken?.(action.value);
    },
  });

  // Locked variant for officers without permission — explains why
  if (!allowed) {
    return (
      <Button
        data-testid={`hc-phase4-action-${action.value}-locked`}
        variant="ghost"
        size="sm"
        disabled
        title="Only Commander or Commander in Chief can take this action"
        className="h-7 text-xs text-slate-600 cursor-not-allowed px-2"
      >
        <Lock className="w-3 h-3 mr-1" />
        {action.label}
      </Button>
    );
  }

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <Button
          data-testid={`hc-phase4-action-${action.value}-trigger`}
          variant="ghost"
          size="sm"
          className={`h-7 text-xs px-2 ${colors.btn}`}
        >
          <Icon className="w-3.5 h-3.5 mr-1" />
          {action.label}
          {expanded ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronRight className="w-3 h-3 ml-1" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className={`mt-3 p-3 rounded-lg border space-y-3 ${colors.panel}`}>
          <p className={`text-[11px] ${colors.accent}`}>{action.description}</p>

          <div className="space-y-1.5">
            <Label className={`text-xs font-semibold ${colors.accent}`}>Reason *</Label>
            <ApproachChips
              options={action.reasons}
              value={reason}
              onChange={setReason}
              accentColorClass={`${colors.accent} bg-white/[0.06] border-white/15`}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Notes (optional)</Label>
            <Textarea
              data-testid={`hc-phase4-action-${action.value}-notes`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200 text-xs"
              rows={3}
              placeholder="Additional context for the submitter..."
            />
          </div>

          <div className="flex gap-2">
            <Button
              data-testid={`hc-phase4-action-${action.value}-confirm`}
              size="sm"
              disabled={!reason || actionMutation.isPending}
              onClick={() => actionMutation.mutate()}
              className={`text-white h-7 text-xs flex-1 ${colors.cta}`}
            >
              <Icon className="w-3 h-3 mr-1" />
              {actionMutation.isPending ? 'Submitting...' : `Confirm ${action.label}`}
            </Button>
            <Button
              data-testid={`hc-phase4-action-${action.value}-cancel`}
              size="sm"
              variant="ghost"
              onClick={() => { setExpanded(false); setReason(''); setNotes(''); }}
              className="h-7 text-xs text-slate-400"
            >
              Cancel
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Phase4ActionBar ────────────────────────────────────────────────────
// Renders all 5 Phase 4 action buttons in a horizontal strip.
// Sits at the bottom of both BalloonDetailPanel and RockDetailPanel, below the regular Save row.
function Phase4ActionBar({ issue, officers, userProfile, onActionTaken }) {
  const allowed = canTakePhase4Actions(userProfile);

  return (
    <div className="mt-4 pt-4 border-t border-white/[0.06]" data-testid="hc-phase4-action-bar">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
          Other Council Actions
        </Label>
        {!allowed && (
          <span className="text-[10px] text-slate-600 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Commander only
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PHASE4_ACTIONS.map(action => (
          <Phase4ActionPanel
            key={action.value}
            action={action}
            issue={issue}
            officers={officers}
            userProfile={userProfile}
            onActionTaken={onActionTaken}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Issue List Item ────────────────────────────────────────────────────
function IssueListItem({ issue, isSelected, onClick, isBalloon, compact, officers }) {
  const complete = isBalloon ? isBalloonComplete(issue) : isRockComplete(issue);

  const getOfficerCallsign = (email) => {
    if (!email || !officers?.length) return null;
    const officer = officers.find(o => o.user_email === email);
    return officer ? officer.callsign : email.split('@')[0];
  };

  if (compact) {
    return (
      <button data-testid={`hc-issue-card-${issue.id}`} onClick={onClick}
        className={`
          w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all duration-150 border
          ${isSelected
            ? 'bg-indigo-500/10 border-indigo-500/30 shadow-[0_0_0_1px_rgba(99,102,241,0.15)]'
            : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.05] hover:border-white/[0.08]'}`}
      >
        {complete
          ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
          : <Circle className="w-4 h-4 text-white/15 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-200 font-medium truncate">{issue.title}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : ''}
          </div>
        </div>
        {getSeverityBadge(issue)}
      </button>
    );
  }

  // Full-width large card
  const ownerSection = isBalloon ? (
    issue.resolution_owner && (
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Owner:</span>
        <span className="text-[11px] text-sky-400 font-medium">{getOfficerCallsign(issue.resolution_owner)}</span>
      </div>
    )
  ) : (
    (issue.rock_track1_owner || issue.rock_track2_owner || issue.rock_track3_owner) && (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
        {issue.rock_track1_owner && (
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-blue-400" />
            <span className="text-[10px] text-blue-400 font-medium">{getOfficerCallsign(issue.rock_track1_owner)}</span>
          </div>
        )}
        {issue.rock_track2_owner && (
          <div className="flex items-center gap-1">
            <Rocket className="w-3 h-3 text-emerald-400" />
            <span className="text-[10px] text-emerald-400 font-medium">{getOfficerCallsign(issue.rock_track2_owner)}</span>
          </div>
        )}
        {issue.rock_track3_owner && (
          <div className="flex items-center gap-1">
            <Zap className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-medium">{getOfficerCallsign(issue.rock_track3_owner)}</span>
          </div>
        )}
      </div>
    )
  );

  return (
    <button data-testid={`hc-issue-card-${issue.id}`} onClick={onClick}
      className="w-[80%] flex items-center gap-4 px-5 py-4 rounded-xl text-left transition-all duration-150 border bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.12] hover:shadow-lg hover:shadow-black/10"
    >
      {complete
        ? <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
        : <Circle className="w-5 h-5 text-white/15 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-200 font-semibold">{issue.title}</div>
        <div className="flex items-center gap-3 mt-1">
          <Link to={createPageUrl('issues') + '?tab=issues&highlight=' + issue.id}
            className="text-[10px] text-slate-500 font-mono hover:text-indigo-400 hover:underline inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}>
            {issue.id} <ExternalLink className="w-2.5 h-2.5" />
          </Link>
          <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') : issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : '-'}
          </span>
        </div>
        {ownerSection}
        {issue.description && (
          <div className="text-xs text-slate-400 mt-1.5 line-clamp-2">{issue.description}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        {getSeverityBadge(issue)}
        {complete && (
          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[9px]">Done</Badge>
        )}
      </div>
    </button>
  );
}

// ─── Issue Lane (Balloon or Rock section in left panel) ─────────────────
function IssueLane({ type, issues, selectedId, onSelect, compact, officers }) {
  const isBalloon = type === 'balloon';
  const icon = isBalloon ? '🎈' : '🪨';
  const label = isBalloon ? 'Balloons' : 'Rocks';
  const accentClass = isBalloon ? 'text-sky-400' : 'text-stone-400';

  if (issues.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-base">{icon}</span>
        <span className={`text-[11px] font-bold ${accentClass} uppercase tracking-widest font-mono`}>{label}</span>
        <span className="text-[10px] text-white/20 font-mono">({issues.length})</span>
      </div>
      <div className={compact ? 'flex flex-col gap-1' : 'flex flex-col gap-2 items-center'}>
        {issues.map(issue => (
          <IssueListItem key={issue.id} issue={issue} isSelected={selectedId === issue.id}
            onClick={() => onSelect(issue.id)} isBalloon={isBalloon} compact={compact} officers={officers} />
        ))}
      </div>
    </div>
  );
}

// ─── Balloon Detail Panel ───────────────────────────────────────────────
function BalloonDetailPanel({ issue, officers, userProfile, onSaved, onActionTaken }) {
  const queryClient = useQueryClient();
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [showIntel, setShowIntel] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });
  const [formData, setFormData] = useState({
    decision: issue.decision || '',
    resolution_owner: issue.resolution_owner || '',
    resolution_timeline: issue.resolution_timeline || '',
    resolution_success_criteria: issue.resolution_success_criteria || '',
    decision_notes: issue.decision_notes || '',
  });

  // Reset form when switching issues OR when server data arrives after save
  React.useEffect(() => {
    setFormData({
      decision: issue.decision || '',
      resolution_owner: issue.resolution_owner || '',
      resolution_timeline: issue.resolution_timeline || '',
      resolution_success_criteria: issue.resolution_success_criteria || '',
      decision_notes: issue.decision_notes || '',
    });
    setShowSaved(false);
  }, [issue.id, issue.decision, issue.resolution_owner, issue.resolution_timeline, issue.resolution_success_criteria, issue.decision_notes]);

  const saveMutation = useMutation({
    mutationFn: (data) => base44.entities.Issue.update(issue.id, { ...data, status: 'decision_made' }),
    onSuccess: (_, savedData) => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      // Show save confirmation toast
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
      onSaved?.(savedData);
      if (issue.reported_by) {
        const resolver = officers?.find(o => o.user_email === user?.email);
        const ownerOfficer = officers?.find(o => o.user_email === savedData.resolution_owner);
        const approachLabel = BALLOON_APPROACHES.find(a => a.value === savedData.decision)?.label || savedData.decision;
        base44.functions.invoke('sendNotification', {
          recipient_email: issue.reported_by, type: 'issue_resolution_decided',
          source_type: 'issue', source_id: issue.id, source_name: issue.title,
          triggered_by_email: user?.email,
          metadata: { issue_title: issue.title, issue_type: 'balloon', resolution_approach: approachLabel,
            resolution_owner: ownerOfficer?.callsign || savedData.resolution_owner || 'Unassigned',
            resolution_timeline: savedData.resolution_timeline || '', resolved_by_name: resolver?.callsign || user?.email,
            decision_notes: savedData.decision_notes || '' }
        }).catch(e => console.warn('Resolution notification failed:', e.message));
      }
    },
  });

  const officerOptions = officers.map(o => ({ value: o.user_email, label: `${o.callsign} (${o.rank_title})` }));

  return (
    <div className="animate-in fade-in-0 slide-in-from-right-2 duration-200">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xl">🎈</span>
          <Link to={createPageUrl('issues') + '?tab=issues&highlight=' + issue.id}
            className="text-[10px] text-slate-500 font-mono hover:text-indigo-400 hover:underline inline-flex items-center gap-1">
            {issue.id} <ExternalLink className="w-2.5 h-2.5" />
          </Link>
          {getSeverityBadge(issue)}
          {isBalloonComplete(issue) && (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[9px]">
              <CheckCircle className="w-3 h-3 mr-1" /> Complete
            </Badge>
          )}
        </div>
        <h3 className="text-[15px] font-semibold text-white leading-snug">{issue.title}</h3>
        <button data-testid="hc-show-issue-intel-btn" onClick={() => setShowIntel(v => !v)}
          className={`mt-2.5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all border shadow-lg ${showIntel ? 'bg-indigo-500/25 text-indigo-200 border-indigo-400/40 shadow-indigo-500/20' : 'bg-indigo-600/20 text-indigo-300 border-indigo-500/30 shadow-indigo-500/15 hover:bg-indigo-500/30 hover:shadow-indigo-500/25 hover:text-indigo-200'}`}>
          <Info className="w-4 h-4" />
          {showIntel ? 'Hide Issue Intel' : 'Show Issue Intel'}
        </button>
        {showIntel && <IssueIntelPanel issue={issue} />}
      </div>

      {/* Approach — chip selector instead of dropdown */}
      <div className="mb-4">
        <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-2 block">Resolution Approach</Label>
        <ApproachChips options={BALLOON_APPROACHES} value={formData.decision}
          onChange={(v) => setFormData(p => ({ ...p, decision: v }))}
          accentColorClass="text-sky-300 bg-sky-500/15 border-sky-500/30" />
      </div>

      {/* Owner */}
      <div className="mb-4">
        <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-2 block">Resolution Owner</Label>
        <SearchableTagSelect options={officerOptions} value={formData.resolution_owner}
          onValueChange={(v) => setFormData(p => ({ ...p, resolution_owner: v }))}
          placeholder="Assign owner..." emptyMessage="No officers." />
      </div>

      {/* Timeline & Success Criteria */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Timeline</Label>
          <Input data-testid="hc-resolution-timeline-input" value={formData.resolution_timeline}
            onChange={(e) => setFormData(p => ({ ...p, resolution_timeline: e.target.value }))}
            className="bg-slate-950 border-slate-700 h-8 text-sm text-slate-50" placeholder="e.g., 1 week" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Done looks like</Label>
          <Input data-testid="hc-success-criteria-input" value={formData.resolution_success_criteria}
            onChange={(e) => setFormData(p => ({ ...p, resolution_success_criteria: e.target.value }))}
            className="bg-slate-950 border-slate-700 h-8 text-sm text-slate-50" placeholder="Success criteria..." />
        </div>
      </div>

      {/* Notes */}
      <div className="mb-5">
        <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Notes</Label>
        <Textarea data-testid="hc-decision-notes-textarea" value={formData.decision_notes} onChange={(e) => setFormData(p => ({ ...p, decision_notes: e.target.value }))}
          className="bg-slate-950 border-slate-700 text-slate-200 text-sm" rows={8} placeholder="Resolution notes..." />
      </div>

      {/* Actions */}
      <div className="flex gap-2 items-center">
        <Button size="sm" onClick={() => saveMutation.mutate(formData)} disabled={saveMutation.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-700 h-9 text-xs font-semibold">
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saveMutation.isPending ? 'Saving...' : 'Save Resolution'}
        </Button>
        <Button data-testid="hc-task-btn" size="sm" variant="outline" onClick={() => setShowTaskCreator(true)}
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-9 text-xs">
          <CheckSquare className="w-3.5 h-3.5 mr-1" /> Task
        </Button>
      </div>
      <div className="mt-2">
        <SaveToast show={showSaved} />
      </div>

      {/* Phase 4 Council Actions (CiC / Commander only) */}
      <Phase4ActionBar issue={issue} officers={officers} userProfile={userProfile} onActionTaken={onActionTaken} />

      <RockTaskCreator open={showTaskCreator} onClose={() => setShowTaskCreator(false)} issueId={issue?.id} officers={officers} />
    </div>
  );
}

// ─── Rock Detail Panel (3-track accordion) ──────────────────────────────
function RockDetailPanel({ issue, officers, policies, capabilities, userProfile, onCreatePolicy, onCreateCapability, onSaved, onActionTaken }) {
  const queryClient = useQueryClient();
  const [expandedTrack, setExpandedTrack] = useState('track1');
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [showActivityCreator, setShowActivityCreator] = useState(false);
  const [showIntel, setShowIntel] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });

  const [formData, setFormData] = useState({
    rock_track1_owner: issue?.rock_track1_owner || '',
    rock_track1_status: issue?.rock_track1_status || 'not_started',
    rock_track1_notes: issue?.rock_track1_notes || '',
    rock_track2_owner: issue?.rock_track2_owner || '',
    rock_track2_status: issue?.rock_track2_status || 'not_started',
    rock_track2_notes: issue?.rock_track2_notes || '',
    rock_track2_pathway: issue?.rock_track2_pathway || '',
    rock_track3_owner: issue?.rock_track3_owner || '',
    rock_track3_status: issue?.rock_track3_status || 'not_started',
    rock_track3_notes: issue?.rock_track3_notes || '',
    decision: issue?.decision || '',
  });

  // Reset when issue changes OR when server data arrives after save
  React.useEffect(() => {
    setFormData({
      rock_track1_owner: issue?.rock_track1_owner || '',
      rock_track1_status: issue?.rock_track1_status || 'not_started',
      rock_track1_notes: issue?.rock_track1_notes || '',
      rock_track2_owner: issue?.rock_track2_owner || '',
      rock_track2_status: issue?.rock_track2_status || 'not_started',
      rock_track2_notes: issue?.rock_track2_notes || '',
      rock_track2_pathway: issue?.rock_track2_pathway || '',
      rock_track3_owner: issue?.rock_track3_owner || '',
      rock_track3_status: issue?.rock_track3_status || 'not_started',
      rock_track3_notes: issue?.rock_track3_notes || '',
      decision: issue?.decision || '',
    });
    setExpandedTrack('track1');
    setShowSaved(false);
  }, [issue?.id, issue?.rock_track1_owner, issue?.rock_track2_owner, issue?.rock_track3_owner, issue?.decision]);

  const linkedPolicy = issue?.linked_policy_id
    ? policies?.find(p => p.id === issue.linked_policy_id)
    : policies?.find(p => (p.linked_issue_ids || []).includes(issue?.id));

  const linkedCapability = issue?.linked_capability_id
    ? capabilities?.find(c => c.id === issue.linked_capability_id)
    : capabilities?.find(c => (c.linked_issue_ids || []).includes(issue?.id));

  const saveMutation = useMutation({
    mutationFn: (data) => base44.entities.Issue.update(issue.id, data),
    onSuccess: (_, savedData) => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      // Show save confirmation toast
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
      onSaved?.(savedData);
      if (issue?.reported_by && savedData.rock_track2_pathway && savedData.decision) {
        const resolver = officers?.find(o => o.user_email === user?.email);
        const pathwayLabel = TRACK2_APPROACHES.find(a => a.value === savedData.rock_track2_pathway)?.label || savedData.rock_track2_pathway;
        const trackOwners = [
          savedData.rock_track1_owner && `Policy: ${officers?.find(o => o.user_email === savedData.rock_track1_owner)?.callsign || savedData.rock_track1_owner}`,
          savedData.rock_track2_owner && `Capability: ${officers?.find(o => o.user_email === savedData.rock_track2_owner)?.callsign || savedData.rock_track2_owner}`,
          savedData.rock_track3_owner && `Operational: ${officers?.find(o => o.user_email === savedData.rock_track3_owner)?.callsign || savedData.rock_track3_owner}`,
        ].filter(Boolean).join(', ');
        base44.functions.invoke('sendNotification', {
          recipient_email: issue.reported_by, type: 'issue_resolution_decided',
          source_type: 'issue', source_id: issue.id, source_name: issue.title,
          triggered_by_email: user?.email,
          metadata: { issue_title: issue.title, issue_type: 'rock', resolution_approach: pathwayLabel,
            resolution_owner: trackOwners || 'Unassigned', resolved_by_name: resolver?.callsign || user?.email,
            decision_notes: savedData.decision || '' }
        }).catch(e => console.warn('Resolution notification failed:', e.message));
      }
    },
  });

  const handleSave = () => {
    const dataToSave = { ...formData };
    if (formData.rock_track2_pathway && formData.decision) {
      dataToSave.status = 'decision_made';
    }
    saveMutation.mutate(dataToSave);
  };

  const officerOptions = (officers || []).map(o => ({ value: o.user_email, label: `${o.callsign} (${o.rank_title})` }));

  // Live progress from local formData
  const getTrackProgressLive = (trackKey) => {
    const ownerKey = `rock_${trackKey}_owner`;
    const statusKey = `rock_${trackKey}_status`;
    let pts = 0;
    if (formData[ownerKey]) pts += 50;
    if (formData[statusKey] === 'in_progress') pts += 25;
    if (formData[statusKey] === 'completed') pts += 50;
    if (trackKey === 'track2' && formData.rock_track2_pathway) pts += 20;
    if (trackKey === 'track3' && formData.decision) pts += 20;
    return Math.min(pts, 100);
  };

  const overallProgress = Math.round(
    ROCK_TRACKS.reduce((sum, t) => sum + getTrackProgressLive(t.key), 0) / ROCK_TRACKS.length
  );

  const trackColorMap = {
    blue: { tab: 'bg-blue-500/12 border-blue-500/35', tabText: 'text-blue-400', panel: 'bg-blue-950/15 border-blue-500/20', chip: 'text-blue-300 bg-blue-500/15 border-blue-500/30', bar: 'bg-blue-500' },
    emerald: { tab: 'bg-emerald-500/12 border-emerald-500/35', tabText: 'text-emerald-400', panel: 'bg-emerald-950/15 border-emerald-500/20', chip: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', bar: 'bg-emerald-500' },
    amber: { tab: 'bg-amber-500/12 border-amber-500/35', tabText: 'text-amber-400', panel: 'bg-amber-950/15 border-amber-500/20', chip: 'text-amber-300 bg-amber-500/15 border-amber-500/30', bar: 'bg-amber-500' },
  };

  return (
    <div className="animate-in fade-in-0 slide-in-from-right-2 duration-200">
      {/* Header with progress ring */}
      <div className="flex items-start gap-3 mb-5">
        <ProgressRing progress={overallProgress} size={48} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">🪨</span>
            <Link to={createPageUrl('issues') + '?tab=issues&highlight=' + issue.id}
              className="text-[10px] text-slate-500 font-mono hover:text-indigo-400 hover:underline inline-flex items-center gap-1">
              {issue.id} <ExternalLink className="w-2.5 h-2.5" />
            </Link>
            {getSeverityBadge(issue)}
            {isRockComplete(issue) && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[9px]">
                <CheckCircle className="w-3 h-3 mr-1" /> Complete
              </Badge>
            )}
          </div>
          <h3 className="text-[15px] font-semibold text-white leading-snug">{issue.title}</h3>
          <button onClick={() => setShowIntel(v => !v)}
            className={`mt-2.5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all border shadow-lg ${showIntel ? 'bg-indigo-500/25 text-indigo-200 border-indigo-400/40 shadow-indigo-500/20' : 'bg-indigo-600/20 text-indigo-300 border-indigo-500/30 shadow-indigo-500/15 hover:bg-indigo-500/30 hover:shadow-indigo-500/25 hover:text-indigo-200'}`}>
            <Info className="w-4 h-4" />
            {showIntel ? 'Hide Issue Intel' : 'Show Issue Intel'}
          </button>
          {showIntel && <IssueIntelPanel issue={issue} />}
        </div>
      </div>

      {/* 3-Track Tab Bar with mini progress */}
      <div className="flex gap-1 mb-4">
        {ROCK_TRACKS.map(track => {
          const Icon = track.icon;
          const progress = getTrackProgressLive(track.key);
          const isActive = expandedTrack === track.key;
          const colors = trackColorMap[track.color];
          return (
            <button key={track.key} onClick={() => setExpandedTrack(isActive ? null : track.key)}
              className={`flex-1 flex flex-col items-center gap-1 py-2 px-2 rounded-lg border transition-all duration-200 cursor-pointer
                ${isActive ? colors.tab : 'bg-white/[0.02] border-white/[0.05] hover:bg-white/[0.05]'}`}>
              <Icon className={`w-4 h-4 ${isActive ? colors.tabText : 'text-slate-500'}`} />
              <span className={`text-[10px] font-bold ${isActive ? colors.tabText : 'text-slate-500'}`}>{track.label}</span>
              <div className="w-full h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                <div className={`h-full rounded-full ${colors.bar} transition-all duration-400`} style={{ width: `${progress}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Expanded Track Panel */}
      {expandedTrack && (() => {
        const track = ROCK_TRACKS.find(t => t.key === expandedTrack);
        const Icon = track.icon;
        const colors = trackColorMap[track.color];
        const ownerKey = `rock_${track.key}_owner`;
        const statusKey = `rock_${track.key}_status`;
        const notesKey = `rock_${track.key}_notes`;

        return (
          <div key={track.key} className={`rounded-xl border p-4 mb-4 animate-in fade-in-0 slide-in-from-bottom-1 duration-200 ${colors.panel}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon className={`w-4 h-4 ${colors.tabText}`} />
              <span className={`text-sm font-bold ${colors.tabText}`}>{track.label}</span>
            </div>
            <p className="text-xs text-slate-400 italic mb-4">{track.question}</p>

            {/* Track 1: Policy link */}
            {track.key === 'track1' && (
              <div className="mb-3">
                {linkedPolicy ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-500/20 bg-blue-950/20">
                    <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Linked Policy</Badge>
                    <span className="text-xs text-blue-300 truncate">{linkedPolicy.title}</span>
                  </div>
                ) : (
                  <Button data-testid="hc-create-bridging-policy-btn" size="sm" variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-7 text-xs"
                    onClick={() => onCreatePolicy?.(issue.id)}>
                    <Shield className="w-3 h-3 mr-1" /> Create Bridging Policy
                  </Button>
                )}
              </div>
            )}

            {/* Track 2: Capability link */}
            {track.key === 'track2' && (
              <div className="mb-3">
                {linkedCapability ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg border border-emerald-500/20 bg-emerald-950/20">
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Linked Capability</Badge>
                    <span className="text-xs text-emerald-300 truncate">{linkedCapability.name}</span>
                  </div>
                ) : (
                  <Button data-testid="hc-request-capability-btn" size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-7 text-xs"
                    onClick={() => onCreateCapability?.(issue.id)}>
                    <Rocket className="w-3 h-3 mr-1" /> Request Capability
                  </Button>
                )}
              </div>
            )}

            {/* Approach chips if applicable */}
            {track.approaches && (
              <div className="mb-3">
                <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Approach</Label>
                <ApproachChips options={track.approaches}
                  value={track.key === 'track2' ? formData.rock_track2_pathway : formData.decision}
                  onChange={(v) => setFormData(p => ({ ...p, ...(track.key === 'track2' ? { rock_track2_pathway: v } : { decision: v }) }))}
                  accentColorClass={colors.chip} />
              </div>
            )}

            {/* Owner */}
            <div className="mb-3">
              <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Track Owner</Label>
              <SearchableTagSelect options={officerOptions} value={formData[ownerKey]}
                onValueChange={(v) => setFormData(p => ({ ...p, [ownerKey]: v }))}
                placeholder="Assign owner..." emptyMessage="No officers." />
            </div>

            {/* Status — chip selector instead of dropdown */}
            <div className="mb-3">
              <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Status</Label>
              <StatusChips value={formData[statusKey]} onChange={(v) => setFormData(p => ({ ...p, [statusKey]: v }))} />
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest mb-1.5 block">Notes</Label>
              <Textarea data-testid="hc-track-notes-textarea" value={formData[notesKey]} onChange={(e) => setFormData(p => ({ ...p, [notesKey]: e.target.value }))}
                className="bg-slate-950 border-slate-700 text-slate-200 text-sm" rows={8} placeholder={`${track.label} notes...`} />
            </div>
          </div>
        );
      })()}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button data-testid="hc-save-all-tracks-btn" onClick={handleSave} disabled={saveMutation.isPending}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-xs font-semibold">
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saveMutation.isPending ? 'Saving...' : 'Save All Tracks'}
        </Button>
        <Button data-testid="hc-task-btn-1" size="sm" variant="outline" onClick={() => setShowTaskCreator(true)}
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-9 text-xs">
          <CheckSquare className="w-3.5 h-3.5 mr-1" /> Task
        </Button>
        <Button data-testid="hc-activity-btn" size="sm" variant="outline" onClick={() => setShowActivityCreator(true)}
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 h-9 text-xs">
          <Activity className="w-3.5 h-3.5 mr-1" /> Activity
        </Button>
      </div>
      <div className="mt-2">
        <SaveToast show={showSaved} />
      </div>

      {/* Phase 4 Council Actions (CiC / Commander only) */}
      <Phase4ActionBar issue={issue} officers={officers} userProfile={userProfile} onActionTaken={onActionTaken} />

      <RockTaskCreator open={showTaskCreator} onClose={() => setShowTaskCreator(false)} issueId={issue?.id} officers={officers} />
      <RockActivityCreator open={showActivityCreator} onClose={() => setShowActivityCreator(false)} issueId={issue?.id} officers={officers} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ██  MAIN EXPORT — Phase4Resolution (Master-Detail Layout)
// ═══════════════════════════════════════════════════════════════════════
export default function Phase4Resolution({ issues, officers, policies, capabilities, objectives, activities, userProfile, onProceed }) {
  const balloons = useMemo(() => issues.filter(i => i.classification_gate2 === 'balloon'), [issues]);
  const rocks = useMemo(() => issues.filter(i => i.classification_gate2 === 'rock'), [issues]);
  const allIssues = useMemo(() => [...balloons, ...rocks], [balloons, rocks]);

  const [selectedId, setSelectedId] = useState(null);
  const selected = selectedId ? issues.find(i => i.id === selectedId) : null;

  // ─── Filters ────────────────────────────────────────────────────────────
  const [filterOwner, setFilterOwner] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');

  const matchesOwner = (issue) => {
    if (!filterOwner) return true;
    return (
      issue.resolution_owner === filterOwner ||
      issue.rock_track1_owner === filterOwner ||
      issue.rock_track2_owner === filterOwner ||
      issue.rock_track3_owner === filterOwner
    );
  };
  const matchesSeverity = (issue) => {
    if (!filterSeverity) return true;
    return (issue.severity || issue.impact_level || 'medium') === filterSeverity;
  };
  const filteredBalloons = useMemo(
    () => balloons.filter(i => matchesOwner(i) && matchesSeverity(i)),
    [balloons, filterOwner, filterSeverity]
  );
  const filteredRocks = useMemo(
    () => rocks.filter(i => matchesOwner(i) && matchesSeverity(i)),
    [rocks, filterOwner, filterSeverity]
  );
  const filteredAllIssues = useMemo(() => [...filteredBalloons, ...filteredRocks], [filteredBalloons, filteredRocks]);
  const filtersActive = !!(filterOwner || filterSeverity);

  // Policy / Capability dialog state
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [policyIssueId, setPolicyIssueId] = useState(null);
  const [showCapabilityDialog, setShowCapabilityDialog] = useState(false);
  const [capabilityIssueId, setCapabilityIssueId] = useState(null);

  const handleCreatePolicy = (issueId) => { setPolicyIssueId(issueId); setShowPolicyDialog(true); };
  const handleCreateCapability = (issueId) => { setCapabilityIssueId(issueId); setShowCapabilityDialog(true); };

  // Completion — always based on full (unfiltered) sets
  const allBalloonsComplete = balloons.every(isBalloonComplete);
  const allRocksComplete = rocks.every(isRockComplete);
  const allDone = (balloons.length === 0 || allBalloonsComplete) && (rocks.length === 0 || allRocksComplete);

  // Overall progress for the progress bar — always based on full sets
  const totalSlots = balloons.length * 2 + rocks.length * 3;
  const filledSlots =
    balloons.filter(i => i.resolution_owner).length +
    balloons.filter(i => i.decision).length +
    rocks.reduce((sum, i) => sum + (i.rock_track1_owner ? 1 : 0) + (i.rock_track2_owner ? 1 : 0) + (i.rock_track3_owner ? 1 : 0), 0);
  const overallProgress = totalSlots ? Math.round((filledSlots / totalSlots) * 100) : 0;

  // Auto-advance to next incomplete issue after save
  // savedData is the formData snapshot at save time — don't rely on stale `selected`
  const handleIssueSaved = (savedData) => {
    if (!selectedId) return;
    const currentIssue = issues.find(i => i.id === selectedId);
    if (!currentIssue) return;
    // Merge saved data over current issue to check completion against what was just persisted
    const merged = { ...currentIssue, ...(savedData || {}) };
    const complete = merged.classification_gate2 === 'balloon' ? isBalloonComplete(merged) : isRockComplete(merged);
    if (complete) {
      const next = filteredAllIssues.find(i => {
        if (i.id === selectedId) return false;
        return i.classification_gate2 === 'balloon' ? !isBalloonComplete(i) : !isRockComplete(i);
      });
      if (next) setSelectedId(next.id);
    }
  };

  // When a Phase 4 action is taken (send back / dismiss / defer / escalate / convert),
  // close the dialog. The Issue entity has been updated and react-query has been invalidated,
  // so the lane lists will refresh automatically. For 'convert', the issue stays in the list
  // but moves between balloon/rock lanes — closing the dialog is the right UX.
  const handlePhase4Action = (_actionValue) => {
    setSelectedId(null);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center mb-2">
        <Badge variant="outline" className="text-indigo-400 border-indigo-500/30 bg-indigo-500/10 text-[10px] font-mono font-bold tracking-widest mb-2">
          PHASE 4
        </Badge>
        <h2 className="text-xl font-bold text-white">Resolution Routing</h2>
        <p className="text-sm text-slate-400">Assign owners and define resolution paths • 15-25 min</p>
      </div>

      {/* Overall Progress Bar */}
      <div className="flex items-center gap-3 p-2.5 px-4 rounded-xl glass-panel">
        <ProgressRing progress={overallProgress} size={38} strokeWidth={2.5} />
        <div className="flex-1 min-w-0">
          <div className="flex gap-5 text-xs mb-1.5">
            <span className="text-slate-400">
              🎈 <span className={`font-semibold ${allBalloonsComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
                {balloons.filter(isBalloonComplete).length}/{balloons.length}
              </span>
            </span>
            <span className="text-slate-400">
              🪨 <span className={`font-semibold ${allRocksComplete ? 'text-emerald-400' : 'text-amber-400'}`}>
                {rocks.filter(isRockComplete).length}/{rocks.length}
              </span>
            </span>
          </div>
          <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-indigo-500'}`}
              style={{ width: `${overallProgress}%` }} />
          </div>
        </div>
      </div>

      {/* ─── Filter Bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 p-3 px-4 rounded-xl glass-panel">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest shrink-0">Filter</span>

        {/* Owner filter */}
        <div className="flex-1 min-w-[160px] max-w-[220px]">
          <SearchableTagSelect
            options={[{ value: '', label: 'All Owners' }, ...officers.map(o => ({ value: o.user_email, label: o.callsign || o.user_email }))]}
            value={filterOwner}
            onValueChange={setFilterOwner}
            placeholder="Owner…"
            emptyMessage="No officers."
          />
        </div>

        {/* Criticality / Severity filter */}
        <div className="flex items-center gap-1.5">
          {[
            { value: '', label: 'All' },
            { value: 'critical', label: '🔴 Critical' },
            { value: 'high', label: '🟠 High' },
            { value: 'medium', label: '🟡 Medium' },
            { value: 'low', label: '⚪ Low' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterSeverity(opt.value)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition-colors ${
                filterSeverity === opt.value
                  ? 'bg-indigo-500/25 border-indigo-500/50 text-indigo-300'
                  : 'bg-white/[0.04] border-white/[0.06] text-slate-400 hover:text-slate-300 hover:border-white/[0.12]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Clear */}
        {filtersActive && (
          <button
            onClick={() => { setFilterOwner(''); setFilterSeverity(''); }}
            className="ml-auto text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-1 transition-colors"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}

        {/* Result count when filtering */}
        {filtersActive && (
          <span className="text-[10px] text-slate-500 font-mono">
            {filteredAllIssues.length} of {allIssues.length}
          </span>
        )}
      </div>

      {/* ═══ Full-width issue list (always visible) ═══ */}
      <div className="glass-panel rounded-xl p-4 space-y-5">
        <IssueLane type="balloon" issues={filteredBalloons} selectedId={null} onSelect={setSelectedId} compact={false} officers={officers} />
        {filteredBalloons.length > 0 && filteredRocks.length > 0 && <div className="h-px bg-white/[0.06]" />}
        <IssueLane type="rock" issues={filteredRocks} selectedId={null} onSelect={setSelectedId} compact={false} officers={officers} />
        {filteredAllIssues.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <AlertCircle className="w-8 h-8 mb-3 text-slate-600" />
            <p className="text-sm">{filtersActive ? 'No issues match the active filters' : 'No issues to route in this session'}</p>
          </div>
        )}
      </div>

      {/* ═══ Detail Modal ═══ */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
        <DialogContent data-testid="hc-issue-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 p-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full">
          {selected && (
            selected.classification_gate2 === 'balloon' ? (
              <BalloonDetailPanel issue={selected} officers={officers} userProfile={userProfile} onSaved={handleIssueSaved} onActionTaken={handlePhase4Action} />
            ) : (
              <RockDetailPanel issue={selected} officers={officers} policies={policies} capabilities={capabilities} userProfile={userProfile}
                onCreatePolicy={handleCreatePolicy} onCreateCapability={handleCreateCapability} onSaved={handleIssueSaved} onActionTaken={handlePhase4Action} />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Proceed Button */}
      <Button onClick={onProceed} disabled={!allDone}
        className={`w-full h-12 text-base font-semibold transition-all duration-300
          ${allDone
            ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white shadow-lg shadow-indigo-500/25'
            : 'bg-white/[0.04] text-slate-500 border border-white/[0.06] disabled:opacity-100'}`}>
        {allDone ? (<>Proceed to Closing <ArrowRight className="w-5 h-5 ml-2" /></>) : `Assign all owners first (${overallProgress}% complete)`}
      </Button>

      {/* Dialogs */}
      <PolicyDialog open={showPolicyDialog} onClose={() => setShowPolicyDialog(false)}
        policy={{ linked_issue_ids: policyIssueId ? [policyIssueId] : [], interim_for_capability_development: true }}
        officers={officers} issues={issues} capabilities={capabilities} />
      <CapabilityDialog open={showCapabilityDialog} onClose={() => setShowCapabilityDialog(false)}
        capability={{ linked_issue_ids: capabilityIssueId ? [capabilityIssueId] : [] }}
        officers={officers} issues={issues} policies={policies} objectives={objectives || []} />
    </div>
  );
}
